import {useCallback, useEffect, useRef, useState} from 'react'
import _ from 'lodash'
// Components
import ClientText from '../client-text'
// Types
import type {AdditionalFormField, BallotAdditionalFormField, StripePayload, UploadcareFile} from '../../types/submission-wizard'
// Styles
import styles from '../submission-wizard/demonstrate-success-step.module.scss'
import wizardStyles from '../submission-wizard/submission-wizard.module.scss'

interface DemonstrateSuccessStepProps {
  ballotAdditionalFormFields: BallotAdditionalFormField[]
  stripePayload: StripePayload
  uploadcareFiles: UploadcareFile[]
  isPaidSubmission: boolean
  successMessage: string
}

const DemonstrateSuccessStep = ({
  ballotAdditionalFormFields,
  stripePayload,
  uploadcareFiles,
  isPaidSubmission,
  successMessage,
}: DemonstrateSuccessStepProps) => {
  const stepRef = useRef<HTMLDivElement>(null)

  const [requiresScrolling, setRequiresScrolling] = useState(false)

  const handleWindowResize = useCallback(() => {
    if (!stepRef?.current) return
    const rect = stepRef.current.getBoundingClientRect()
    setRequiresScrolling(rect?.height + 118 > window.innerHeight)
  }, [])

  useEffect(() => {
    // Initialize once on mount
    handleWindowResize()
    // Attach and clean up the window resize listener
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [handleWindowResize])

  const handleElementResize = useCallback((entry: ResizeObserverEntry) => {
    const {height} = entry.contentRect
    const containerHeight = stepRef.current?.getBoundingClientRect()?.height || 0
    const offset = containerHeight - height + 118
    setRequiresScrolling(height + offset > window.innerHeight)
  }, [])

  useEffect(() => {
    const node = stepRef.current
    if (!node || typeof ResizeObserver === 'undefined') return

    const timer = setTimeout(() => {
      handleWindowResize()
    }, 100)

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        handleElementResize(entry)
      }
    })
    resizeObserver.observe(node)

    return () => {
      clearTimeout(timer)
      resizeObserver.disconnect()
    }
  }, [handleElementResize, handleWindowResize])

  return (
    <div
      className={`
        ${wizardStyles['__step-container']}
        ${styles['__container']}
        ${requiresScrolling ? styles['--requires-scrolling'] : ''}
      `}
    >
      <div
        ref={stepRef}
        className={`
          ${styles['demonstrate-success-step']}
          ${wizardStyles['__submission-wizard-step']}
        `}
      >
        <h1 className={styles['__banner-header']}>Your submission has been received!</h1>
        <h2 className={wizardStyles['__step-header']}>Submission Details</h2>
        <div className={wizardStyles['__step-thumbnail-wrapper']}>
          <img
            src={_.first(uploadcareFiles)?.cdnUrl || 'https://ucarecdn.com/47a41f84-b865-4aeb-a53a-3b20cb168d46/-/preview/50x50/'}
            alt={stripePayload?.submissionName || 'Unnamed entry'}
            className={wizardStyles['__entry-thumbnail']}
          />
        </div>
        <div className={wizardStyles['__entry-details']}>
          <h3 className={wizardStyles['__entry-name']}>{stripePayload?.submissionName || 'Unnamed entry'}</h3>
          <h3 className={wizardStyles['__entry-length']}>
            {`${uploadcareFiles.length || '0'} photo${uploadcareFiles.length > 1 || !uploadcareFiles.length ? 's' : ''}`}
          </h3>
        </div>
        <div className={styles['__additional-fields-table']}>
          {_.map(stripePayload.additionalFormFields, (formField: AdditionalFormField) => {
            let value: string | boolean | number | undefined = formField.value
            if (formField.type === 'checkbox') {
              value = _.upperFirst(formField.value ? 'Yes' : 'No')
            }
            if (formField.type === 'selectdropdown') {
              const foundBallotFormField = _.find(ballotAdditionalFormFields, (ballotFormField: BallotAdditionalFormField) => {
                return ballotFormField.type === 'selectdropdown' && ballotFormField.name === formField.name
              })
              value = _.nth(foundBallotFormField?.drop_down_choices, (formField.value as number) - 1)?.name
            }
            if (value) {
              return <div key={formField.id} className={styles['__row']}>
                <div className={styles['__key']}>{formField.name}:</div>
                <div className={styles['__value']}>{
                  formField.type === 'imageupload'
                    ? <img className={styles['__value-image']} src={value as string} />
                    : String(value)
                }</div>
              </div>
            }
          })}
        </div>
        {isPaidSubmission &&
          <div className={wizardStyles['__success-message']}>{successMessage}</div>
        }
        {!isPaidSubmission &&
          <ClientText clientText={successMessage} />
        }
      </div>
    </div>
  )
}

export default DemonstrateSuccessStep
